import { useCallback, useSyncExternalStore } from "react";
import { useGlobalQuery } from "../../api/useGlobalQuery";
import { useRegionReach } from "./useRegionReach";
import { useAppData } from "../../state/AppData";
import { COLOUR_BY_STORAGE_KEY, effectiveColourBy, isMapScopes, parseColourBy, scopesAvailable, type ColourBy, type MapScopesPayload } from "../../lib/mapScopes";

// The map's "Colour by" choice, kept per viewer in localStorage like the map's other view options
// (protocol filter, provenance scope). One shared store so the legend control and the map layer agree.
const listeners = new Set<() => void>();
let current: ColourBy | null = null;

function read(): ColourBy {
  if (current === null) {
    try {
      current = parseColourBy(localStorage.getItem(COLOUR_BY_STORAGE_KEY));
    } catch {
      current = "role"; // private-mode / blocked storage: fine, just don't persist
    }
  }
  return current;
}

function write(next: ColourBy) {
  current = next;
  try {
    localStorage.setItem(COLOUR_BY_STORAGE_KEY, next);
  } catch {
    /* same as above */
  }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export interface MapScopesState {
  /** The mode in force: Scope shows as Role while the protocol filter hides MeshCore. */
  colourBy: ColourBy;
  /** False while the protocol filter is Meshtastic only: no Scope colouring, no Region reach. */
  scopesAvailable: boolean;
  setColourBy: (c: ColourBy) => void;
  /** The stored scope map; null until the first read lands, or while the Audit table has not been calculated yet. */
  view: MapScopesPayload | null;
  /** The stored scope map whenever it is loaded (Scope mode or a Region reach choice), for scope colours. */
  colours: MapScopesPayload | null;
  /** True while the Audit table has no stored result yet (first calculation). */
  pending: boolean;
}

/** The colour-by choice, plus the stored scope map once Scope mode is on (nothing is fetched in Role mode). */
export function useMapScopes(): MapScopesState {
  const chosen = useSyncExternalStore(subscribe, read, () => "role" as ColourBy);
  const { protocolFilter } = useAppData();
  const available = scopesAvailable(protocolFilter);
  const colourBy = effectiveColourBy(chosen, protocolFilter);
  const setColourBy = useCallback((c: ColourBy) => write(c), []);
  const { selected: reachSelected } = useRegionReach();
  const q = useGlobalQuery<MapScopesPayload | { pending: true }>(
    ["global", "mapScopes"], "/api/audit/map-scopes", 60_000, available && (colourBy === "scope" || reachSelected !== null), false,
  );
  const view = isMapScopes(q.data) ? q.data : null;
  const pending = !!q.data && (q.data as { pending?: boolean }).pending === true;
  return { colourBy, scopesAvailable: available, setColourBy, view: colourBy === "scope" ? view : null, colours: available ? view : null, pending };
}
